/**
 * @file 教师关联学员列表弹窗
 * @description 展示该教师名下学员，列与学员管理列表一致（无筛选、无操作）。
 *
 * @whenToUse 教师管理列表「学员人数」入口。
 * @whenNotToUse 学员管理主列表；校区成员名单。
 */

import { Badge, type BadgeVariant } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  computeConsecutiveRowSpan,
  DataTable,
  type DataTableColumn,
} from '@/components/ui/data-table.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  GRADE_OPTIONS,
  LEARNING_LEVEL_OPTIONS,
  MOCK_STUDENTS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  STUDENT_STATUS_LABEL,
  SUBJECT_OPTIONS,
  TEXTBOOK_OPTIONS,
  flattenStudentRows,
  optionLabel,
  type StudentListRow,
  type StudentStatus,
} from '@/components/pages/student-page/mock.ts'
import type { TeacherRow } from '@/components/pages/teacher-page/mock.ts'

const STATUS_BADGE: Record<StudentStatus, BadgeVariant> = {
  normal: 'success',
  trial: 'warning',
  disabled: 'danger',
}

export type TeacherStudentsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  teacher: TeacherRow | null
}

/**
 * 按教师姓名取学员展平行（仅含该教师绑定的科目行）
 * @param teacherName 教师姓名
 */
function listRowsForTeacher(teacherName: string): StudentListRow[] {
  return flattenStudentRows(MOCK_STUDENTS).filter(
    (row) => row.teacherName === teacherName,
  )
}

/**
 * 教师学员名单 Dialog
 */
export function TeacherStudentsDialog({
  open,
  onOpenChange,
  teacher,
}: TeacherStudentsDialogProps) {
  const listData = teacher ? listRowsForTeacher(teacher.name) : []

  const studentRowSpan: DataTableColumn<StudentListRow>['onCell'] = (
    _record,
    index,
    data,
  ) => ({
    rowSpan: computeConsecutiveRowSpan(data, index, (row) => row.studentId),
  })

  const columns: DataTableColumn<StudentListRow>[] = [
    {
      key: 'name',
      title: '学员名称',
      dataIndex: 'name',
      minWidth: 100,
      onCell: studentRowSpan,
    },
    {
      key: 'phone',
      title: '手机号',
      dataIndex: 'phone',
      minWidth: 120,
      onCell: studentRowSpan,
    },
    {
      key: 'points',
      title: '点数',
      dataIndex: 'points',
      minWidth: 72,
      onCell: studentRowSpan,
    },
    {
      key: 'status',
      title: '状态',
      minWidth: 88,
      onCell: studentRowSpan,
      render: (row) => (
        <Badge variant={STATUS_BADGE[row.status]}>
          {STUDENT_STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: 'subject',
      title: '科目',
      minWidth: 72,
      className: 'border-l border-border',
      render: (row) => optionLabel(SUBJECT_OPTIONS, row.subject),
    },
    {
      key: 'textbook',
      title: '教材',
      minWidth: 120,
      render: (row) => optionLabel(TEXTBOOK_OPTIONS, row.textbook),
    },
    {
      key: 'grade',
      title: '年级',
      minWidth: 72,
      render: (row) => optionLabel(GRADE_OPTIONS, row.grade),
    },
    {
      key: 'semester',
      title: '学期',
      minWidth: 72,
      render: (row) => optionLabel(SEMESTER_OPTIONS, row.semester),
    },
    {
      key: 'stage',
      title: '阶段',
      minWidth: 72,
      render: (row) => optionLabel(STAGE_OPTIONS, row.stage),
    },
    {
      key: 'learningLevel',
      title: '学习力等级',
      minWidth: 96,
      render: (row) => optionLabel(LEARNING_LEVEL_OPTIONS, row.learningLevel),
    },
    {
      key: 'learningPower',
      title: '学习力',
      dataIndex: 'learningPower',
      minWidth: 72,
    }
  ]

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${teacher?.name ?? ''} · 学员列表`}
      className="max-w-6xl"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          关闭
        </Button>
      }
    >
      <DataTable
        columns={columns}
        data={listData}
        pagination={{
          page: 1,
          pageSize: 10,
          total: listData.length,
          onChange: (page) => {
            console.log(page)
          },
        }}
      />
    </Dialog>
  )
}
